<script lang="ts">
    import type {Document, DocumentAvailability, Rec} from 'typings/portaro.be.types';
    import type {SearchManager} from '../../search-manager/search-manager';
    import {onDestroy, onMount} from 'svelte';
    import {KpSearchItemDocumentPresenter} from './kp-search-item-document.presenter';
    import {exists, isNullOrUndefined} from 'shared/utils/custom-utils';
    import {byProperty, anyMatches, byIdOf} from 'shared/utils/array-utils';
    import {State} from 'shared/ui-widgets/collapsible-button-menu/types';
    import {fade} from 'svelte/transition';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import KpCover from '../../../record/kp-cover/KpCover.svelte';
    import KpCustomParagraph from 'shared/value-editors/internal/editors/search-or-edit/templates/KpCustomParagraph.svelte';
    import KpAvailabilityInfo from '../../../record/kp-availability-info/KpAvailabilityInfo.svelte';
    import KpFavouritesButton from '../../../favourites/kp-favourites-button/KpFavouritesButton.svelte';
    import KpRecordClipboardButton from '../../../record/kp-record-clipboard/kp-record-clipboard-button/KpRecordClipboardButton.svelte';
    import KpAvailabilityButton from '../../../loan/kp-availability-button/KpAvailabilityButton.svelte';
    import KpFindInDatabases from '../../../record/kp-find-in-databases/KpFindInDatabases.svelte';
    import Labels from 'shared/components/kp-label/Labels.svelte';
    import KpCollapsibleMenuWrapper from 'shared/ui-widgets/menu-wrapper/KpCollapsibleMenuWrapper.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    export let record: Document;
    export let searchManager: SearchManager<Document>;

    const localize = getLocalization();
    const presenter = getInjector().getByToken<KpSearchItemDocumentPresenter>(KpSearchItemDocumentPresenter.presenterName);

    const pagination = searchManager.getPagination();
    const display = {moreParagraph: false};
    const documentLink = presenter.createRecordLink(record, pagination.getPaginationDataObject().items);

    let clipBoardRecords: Rec[] = [];
    let availability: DocumentAvailability;

    const recordsSubscription = presenter.clipboardRecords().subscribe((updatedRecordList) => clipBoardRecords = updatedRecordList);

    $: clipboardSize = clipBoardRecords.length;
    $: isClipboardNotEmpty = clipBoardRecords.length > 0;
    $: isNotIncludedInClipboard = !anyMatches(clipBoardRecords, byIdOf(record));

    onMount(async () => availability = await presenter.getAvailability(record));

    onDestroy(() => recordsSubscription.unsubscribe());

    function isOrderable(documentAvailability: DocumentAvailability | undefined): boolean {
        if (isNullOrUndefined(documentAvailability)) {
            return false;
        }
        return anyMatches(availability.capabilities, byProperty('name', 'standardOrderability'));
    }

    function isDownloadable(documentAvailability: DocumentAvailability | undefined): boolean {
        if (isNullOrUndefined(documentAvailability)) {
            return false;
        }
        return anyMatches(availability.capabilities, byProperty('name', 'downloadability'));
    }

    const handleEditClick = async () => {
        await presenter.createRecordEditation(record, record.fond);
    };
</script>

<KpCollapsibleMenuWrapper>
    <div slot="main" class="list-item-document"
         class:orderable={exists(availability) && isOrderable(availability)}
         class:not-orderable={exists(availability) && !isOrderable(availability)}>

        <div class="list-item-document-left">
            {#if !record.external}
                <div class="cover-container cover-container-document-search">
                    <KpCover record={record} href={documentLink} fillTo='width'/>
                </div>
            {/if}
        </div>

        <div class="list-item-document-main">
            <div class="list-item-document-main-content">
                <h2 class="unset-style nazevZaznamu">
                    {#if record.external}
                        <span>{record.name}</span>
                    {:else}
                        <a href={documentLink}>{record.name}</a>
                    {/if}

                    {#if record.downloadable || isDownloadable(availability)}
                        <img src="/resources/img/icons/ebook.png"
                             width="18"
                             height="18"
                             loading="lazy"
                             alt={localize(/* @kp-localization record.ElektronickaForma */ 'record.ElektronickaForma')}
                             title={localize(/* @kp-localization record.ElektronickaForma */ 'record.ElektronickaForma')}/>
                    {/if}
                </h2>

                {#if record.subtitle && record.subtitle.length > 0}
                    <div class="record-subtitle">
                        <Labels labeleds={record.subtitle} linkifyUrls={true}/>
                    </div>
                {/if}

                {#if record.primaryAuthors && record.primaryAuthors.length > 0}
                    <div class="list-item-document-autor">
                        <Labels labeleds={record.primaryAuthors}/>
                    </div>
                {/if}

                <KpCustomParagraph content={record.exports.portaroSearchItemParagraph} customClass="list-item-document-paragraph-template"/>

                <div class="dostupnost" style="margin-top: 5px;">
                    {#if !isNullOrUndefined(availability)}
                        <KpAvailabilityInfo availability={availability}/>
                    {/if}
                </div>

                {#if record.withForeignOccurrences}
                    <KpFindInDatabases recordId={record.id}/>
                {/if}

                {#if display.moreParagraph && record.exports.portaroSearchItemMoreParagraph}
                    <div transition:fade|global={{duration:200}}>
                        <hr class="smaller-margin"/>
                        <KpCustomParagraph content={record.exports.portaroSearchItemMoreParagraph} customClass="list-item-document-paragraph-template-moreInfo"/>
                    </div>
                {/if}
            </div>
        </div>
    </div>

    <svelte:fragment slot="menu" let:collapsed let:state>
        <li class="collapsible-menu-item">
            <KpAvailabilityButton
                availability={availability}
                on:click={() => presenter.requestDocument(record)}
                forExemplar={false}
                block={true}
                let:label
                let:dataQa
                let:type>

                {#if type === 'available'}
                    <span class="bigger-icon" aria-hidden="true">
                        <UIcon icon="check"/>
                    </span>
                {:else if type === 'reservable'}
                    <span class="bigger-icon" aria-hidden="true">
                        <UIcon icon="clock"/>
                    </span>
                {:else}
                    <span class="bigger-icon" aria-hidden="true">
                        <UIcon icon="cross-circle"/>
                    </span>
                {/if}

                <span class:sr-only={collapsed}
                      class:word-break={state === State.Open}
                      class:no-word-break={state !== State.Open}
                      data-qa={dataQa}>{label}</span>
            </KpAvailabilityButton>
        </li>

        <li class="collapsible-menu-item">
            <div class="jp-favourites-button-container">
                <KpFavouritesButton record={record}>
                    <svelte:fragment slot="add">
                        <span class="glyphicon glyphicon-heart-empty" aria-hidden="true"></span>
                        <span class:sr-only={collapsed}>
                            {localize(/* @kp-localization recordCollections.favourites.AddToFavourites */ 'recordCollections.favourites.AddToFavourites')}
                        </span>
                    </svelte:fragment>

                    <svelte:fragment slot="remove">
                        <span class="glyphicon glyphicon-heart" aria-hidden="true"></span>
                        <span class:sr-only={collapsed}>
                            {localize(/* @kp-localization recordCollections.favourites.RemoveFromFavourites */ 'recordCollections.favourites.RemoveFromFavourites')}
                        </span>
                    </svelte:fragment>
                </KpFavouritesButton>
            </div>
        </li>

        {#if presenter.isLibrarian()}
            <li class="collapsible-menu-item">
                <div class="jp-clipboard-button-container everbis-hidden">
                    <KpRecordClipboardButton record={record}>
                        <svelte:fragment slot="add">
                            <UIcon icon="circle"/>
                            <span class:sr-only={collapsed}>
                                {localize(/* @kp-localization commons.Mark */ 'commons.Mark')}
                            </span>
                        </svelte:fragment>

                        <svelte:fragment slot="remove">
                            <UIcon icon="check-circle"/>
                            <span class:sr-only={collapsed}>
                                {localize(/* @kp-localization commons.Marked */ 'commons.Marked')}
                            </span>
                        </svelte:fragment>
                    </KpRecordClipboardButton>
                </div>
            </li>
        {/if}

        {#if presenter.isLibrarian() && isClipboardNotEmpty && isNotIncludedInClipboard}
            <li transition:fade|global={{duration:200}} class="collapsible-menu-item">
                <div class="everbis-hidden">
                    <button class="btn btn-xs btn-default btn-block" on:click={() => presenter.mergeToThis(record)}>
                        <UIcon icon="file-import"/>
                        <span class:sr-only={collapsed}>
                            {localize(/* @kp-localization record.MergeSelectedToThis */ 'record.MergeSelectedToThis')} ({clipboardSize})
                        </span>
                    </button>
                </div>
            </li>
        {/if}

        {#if presenter.isLibrarian()}
            <li class="collapsible-menu-item">
                <div class="everbis-hidden">
                    <KpButton buttonStyle="primary"
                              buttonSize="xs"
                              isBlock
                              on:click={handleEditClick}>

                        <IconedContent icon="edit">
                            <span class:sr-only={collapsed}>{localize(/* @kp-localization commons.editovat */ 'commons.editovat')}</span>
                        </IconedContent>
                    </KpButton>
                </div>
            </li>
        {/if}

        {#if record.exports.portaroSearchItemMoreParagraph}
            <li class="collapsible-menu-item">
                <div>
                    <button class="btn btn-xs btn-default btn-block" on:click={() => display.moreParagraph = !display.moreParagraph}>
                        <UIcon icon="eye"/>
                        <span class:sr-only={collapsed}>
                            {localize(/* @kp-localization commons.more */ 'commons.more')}
                        </span>
                    </button>
                </div>
            </li>
        {/if}
    </svelte:fragment>
</KpCollapsibleMenuWrapper>

<style lang="less">
    @import (reference) "src/shared/ui-widgets/collapsible-button-menu/collapsible-menu.constants.less";

    .cover-container-document-search {
        width: 94px;
    }

    .no-word-break {
        white-space: unset;
        overflow-wrap: unset;
        word-break: unset;
    }

    .word-break {
        white-space: pre-line;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .smaller-margin {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    .bigger-icon {
        margin-top: 2px;
        font-size: 1.2em;
    }

    .list-item-document {
        display: grid;
        grid-template-columns: 102px auto;
        grid-template-rows: 1fr;
        min-height: 130px;
        position: relative;
        justify-items: end;

        .list-item-document-main {
            grid-column: 2 / span 1;
            grid-row: 1;
            width: 100%;

            .list-item-document-main-content {
                margin-right: @collapsed-width;
            }
        }

        .list-item-document-left {
            grid-column: 1 / span 1;
            margin-right: 8px;
            justify-self: left;
        }
    }
</style>